import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import FeatherIcon from './FeatherIcon';

/** Every product page the nav lists. Adding one here adds it to both the desktop and mobile menus. */
export const PRODUCT_LINKS: readonly { readonly to: string; readonly label: string }[] = [
  { to: '/products/pawsitive-deck', label: 'The Pawsitive Deck' },
];

/**
 * Desktop "Products" dropdown (disclosure pattern): the button toggles a list of links. It closes on
 * Escape (focus back on the button), a click outside it, or a route change.
 */
const ProductsMenu: React.FC = () => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: PointerEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={menuId}
        className={`inline-flex items-center gap-1 transition hover:text-primary ${
          pathname.startsWith('/products') ? 'text-primary' : 'text-gray-700'
        }`}
      >
        Products
        <FeatherIcon name="chevron-down" size={16} className={`inline-flex transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul id={menuId} className="card absolute left-1/2 top-full mt-3 min-w-[14rem] -translate-x-1/2 py-2 shadow-lg">
          {PRODUCT_LINKS.map(({ to, label }) => (
            <li key={to}>
              <Link
                to={to}
                aria-current={pathname === to ? 'page' : undefined}
                className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-primary focus:bg-gray-50 focus:outline-none"
              >
                {label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ProductsMenu;
